import { CheckIcon } from '@heroicons/react/24/outline';
import { createColumnHelper } from '@tanstack/react-table';
import { DateTime } from 'luxon';

import store from 'src/app/store/store';
import { DatePickerColumnFilter } from 'src/common/datagridColumnFilters/DatePickerColumnFilter';
import { DaysOfWeekColumnFilter } from 'src/common/datagridColumnFilters/DaysOfWeekColumnFilter';
import { DriverColumnFilter } from 'src/common/datagridColumnFilters/DriverColumnFilter';
import { RouteTemplateRead } from 'src/common/external/bambi-api/routesApi';

import { routeTemplateActions } from '../routeTemplates.slice';
import { DayOfTheWeekCell } from './DayOfTheWeekCell';
import { ManageCell } from './ManageCell';

const columnHelper = createColumnHelper<RouteTemplateRead>();

export const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('default_driver_name', {
    id: 'driver',
    header: 'Driver',
    cell({ row }) {
      const driverName = row.original.default_driver_name;
      if (!driverName) {
        return <>&mdash;</>;
      }

      return <>{driverName}</>;
    },
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DriverColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().routeTemplates.filterDriver}
            setSelectedAction={routeTemplateActions.setFilterDriver}
          />
        );
      },
      filterCount() {
        return store.getState().routeTemplates.filterDriver.length;
      },
    },
  }),
  columnHelper.accessor('days_of_week', {
    id: 'days',
    header: 'Days',
    cell({ row }) {
      const days = row.original.days_of_week;
      return <DayOfTheWeekCell days={days ?? []} />;
    },
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DaysOfWeekColumnFilter
            open={open}
            onClose={onClose}
            selected={store.getState().routeTemplates.filterDaysOfWeek}
            setSelectedAction={routeTemplateActions.setFilterDaysOfWeek}
          />
        );
      },
      filterCount() {
        return store.getState().routeTemplates.filterDaysOfWeek.length;
      },
    },
  }),
  columnHelper.display({
    id: 'times',
    header: 'Times',
    cell({ row }) {
      const startTime = DateTime.fromISO(
        row.original.start_time
      ).toLocaleString(DateTime.TIME_SIMPLE);
      const endTime = DateTime.fromISO(row.original.end_time).toLocaleString(
        DateTime.TIME_SIMPLE
      );

      return (
        <>
          {startTime} - {endTime}
        </>
      );
    },
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.accessor('range_start', {
    header: 'Start Date',
    cell({ getValue }) {
      const startDate = getValue();
      // range_start is not actually optional, see API

      if (!startDate) {
        return <>&mdash;</>;
      }

      return DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_SHORT);
    },
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DatePickerColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={routeTemplateActions.setFilterRangeStart}
          />
        );
      },
      filterCount() {
        const state = store.getState().routeTemplates;
        return state.filterRangeStart ? 1 : 0;
      },
    },
  }),
  columnHelper.accessor('range_end', {
    header: 'End Date',
    cell({ getValue }) {
      const endDate = getValue();

      if (!endDate) {
        return <>&mdash;</>;
      }

      return DateTime.fromISO(endDate).toLocaleString(DateTime.DATE_SHORT);
    },
    meta: {
      filterComponent(column, open, onClose) {
        return (
          <DatePickerColumnFilter
            open={open}
            onClose={onClose}
            setSelectedAction={routeTemplateActions.setFilterRangeEnd}
          />
        );
      },
      filterCount() {
        const state = store.getState().routeTemplates;
        return state.filterRangeEnd ? 1 : 0;
      },
    },
  }),
  columnHelper.display({
    id: 'is_active',
    header: 'Active',
    cell({ row }) {
      const rangeStart = row.original.range_start;
      const rangeEnd =
        row.original.range_end ?? DateTime.now().plus({ day: 1 }).toISO();
      const today = DateTime.now().toISO();

      if (!rangeStart || !rangeEnd) {
        return <>&mdash;</>;
      }

      const enabled = today >= rangeStart && today <= rangeEnd;

      return (
        <div
          data-testid="active-indicator"
          data-state={enabled ? 'enabled' : 'disabled'}
        >
          {enabled ? (
            <CheckIcon className="w-4 stroke-green-600" />
          ) : (
            <>&mdash;</>
          )}
        </div>
      );
    },
    meta: {
      enableHeaderMenu: false,
    },
  }),
  columnHelper.display({
    id: 'manage',
    header: 'Manage',
    cell({ row }) {
      return <ManageCell routeTemplate={row.original} />;
    },
    meta: {
      enableHeaderMenu: false,
    },
  }),
];
